/* US Stay Pumped BOGO auto-add. Shopify BXGY remains the source of price truth. */
(() => {
  'use strict';
  const WIDGET_ID = '312366';
  const VARIANT_ID = '44333474152557';
  const ENDS_AT = Date.parse('2026-10-30T05:00:00Z');
  const GIFT_FLAG = '_pfm_stay_pumped_gift';
  const attached = new WeakSet();
  let widget;
  let addingUntil = 0;
  let awaitingGift = false;
  let visitorCountry = null;
  let geoRequested = false;
  let timer;

  const isGift = item => item?.properties?.[GIFT_FLAG] === 'true';
  const isStayPumped = item => Number(item?.variant_id || item?.id) === Number(VARIANT_ID);
  const inUSMarket = () => window.Shopify?.country === 'US' && visitorCountry === 'US';

  async function resolveVisitorCountry() {
    if (geoRequested) return;
    // Rebuy can load after this deferred asset on slower connections. Retry until its
    // geolocation API is available rather than treating a loading race as ineligible.
    if (!window.PFMStayPumpedQaGeo && typeof window.Rebuy?.getGeolocation !== 'function') {
      setTimeout(resolveVisitorCountry, 400);
      return;
    }
    geoRequested = true;
    try {
      // Shopify's market selection alone is editable by the visitor. Rebuy supplies
      // the visitor location used by this offer's US-only eligibility rule.
      const location = window.PFMStayPumpedQaGeo || await window.Rebuy?.getGeolocation?.();
      visitorCountry = String(location?.country_code || '').toUpperCase();
    } catch (_) {
      visitorCountry = '';
    }
    synchronize(widget);
  }

  async function getCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to read Shopify cart');
    return response.json();
  }

  async function removeGift(item) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });
    if (!response.ok) throw new Error('Unable to remove Stay Pumped gift');
  }

  function synchronize(current) {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      if (!current || String(current.id) !== WIDGET_ID) return;
      let cart;
      try {
        cart = await getCart();
      } catch (_) {
        return;
      }

      const items = cart.items || [];
      const qualifier = items.some(item => isStayPumped(item) && !isGift(item));
      const gifts = items.filter(isGift);
      if (visitorCountry === null) {
        current.hide?.();
        resolveVisitorCountry();
        return;
      }

      // A gift must never remain without its paid Stay Pumped item or past the offer end.
      if (!inUSMarket() || Date.now() >= ENDS_AT || !qualifier) {
        awaitingGift = false;
        await Promise.all(gifts.map(removeGift));
        current.hide?.();
        return;
      }

      // An existing gift is retained while its paid item remains, even during a cart
      // refresh. This prevents an unnecessary remove/re-add flicker.
      if (gifts.length) {
        awaitingGift = false;
        addingUntil = 0;
        current.hide?.();
        return;
      }

      if (awaitingGift && Date.now() < addingUntil) {
        current.hide?.();
        return;
      }

      awaitingGift = true;
      addingUntil = Date.now() + 12000;
      // Cart Cross-Sell filters its own input product from returned products.
      // Its matching rule still gates eligibility; add the known one-time gift via Rebuy.
      window.Rebuy?.Cart?.addItem?.({
        id: Number(VARIANT_ID),
        quantity: 1,
        properties: {
          _source: 'Rebuy',
          _attribution: 'Rebuy Gift with Purchase',
          _widget_id: WIDGET_ID,
          _r_gwp_hide_qty: 'true',
          [GIFT_FLAG]: 'true'
        }
      });
      current.hide?.();
      setTimeout(() => synchronize(current), 1200);
    }, 350);
  }

  function attach(current) {
    if (!current || String(current.id) !== WIDGET_ID || attached.has(current)) return;
    attached.add(current);
    widget = current;
    current.hide?.();
    synchronize(current);
  }

  ['rebuy.init', 'rebuy.beforeReady', 'rebuy.ready', 'rebuy.productsChange'].forEach(eventName => {
    document.addEventListener(eventName, event => {
      const current = event.detail?.widget;
      attach(current);
      if (String(current?.id) === WIDGET_ID) synchronize(current);
    });
  });
  document.addEventListener('rebuy:cart.change', () => synchronize(widget));
  document.addEventListener('rebuy:cart.add', () => synchronize(widget));
  (window.Rebuy?.widgets || []).forEach(attach);
  resolveVisitorCountry();
  setInterval(() => synchronize(widget), 2000);
})();

